import { CommandEmpty, CommandGroup, CommandItem } from "@hortis/ui/command";
import { Dot } from "@hortis/ui/dot";
import { toast } from "@hortis/ui/sonner";
import { A, F, pipe } from "@mobily/ts-belt";
import { useQueryClient } from "@tanstack/react-query";
import { PlantMaterialStatus, namedOperations } from "generated/graphql";
import { updatePlantMaterialsDocument } from "src/features/actions-menu/graphql";
import { createDateAsUTC } from "src/utils/convert-dates-to-iso";
import { useGqlMutation } from "src/utils/hooks/graphql";
import { twMerge } from "tailwind-merge";

interface CommandMenuStatusPageProps {
  selectedMaterials: Array<{ id: string; status: PlantMaterialStatus }>;
  onSuccess: () => void;
  onAbsentSelect: () => void;
}

const CommandMenuStatusPage = ({
  selectedMaterials,
  onSuccess,
  onAbsentSelect,
}: CommandMenuStatusPageProps) => {
  const queryClient = useQueryClient();
  const updatePlantMaterials = useGqlMutation({
    mutation: updatePlantMaterialsDocument,
    onError: () => {
      toast.error("Failed to update materials");
    },
    onSuccess: (res) => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Tests flake seeming to return nullable removeTagPlantMaterials
      if (!res.updatePlantMaterials?.success) {
        toast.error("Failed to update materials");
        return;
      }
      toast.success(
        `Successfully updated ${
          res.updatePlantMaterials.plantMaterials.length
        } material${
          res.updatePlantMaterials.plantMaterials.length > 1 ? "s" : ""
        }`,
      );
      void queryClient.invalidateQueries({
        queryKey: [namedOperations.Query.PlantMaterials],
      });
      onSuccess();
    },
  });

  const handleStatusSelect = async (
    status: PlantMaterialStatus,
    dateRemoved?: Date,
  ) => {
    const ids = pipe(
      selectedMaterials,
      A.filter((m) => m.status !== status),
      A.map(({ id }) => id),
      F.toMutable,
    );
    if (ids.length === 0) {
      onSuccess();
      return;
    }
    if (status === PlantMaterialStatus.Absent && dateRemoved == null) {
      toast.error("Date removed is required");
      return;
    }
    await updatePlantMaterials.mutateAsync({
      ids,
      input: {
        status,
        firstAbsent:
          status === PlantMaterialStatus.Absent
            ? dateRemoved == null
              ? undefined
              : createDateAsUTC(dateRemoved)
            : null,
      },
    });
  };

  return (
    <>
      <CommandGroup data-cy="command-menu-status-page">
        <CommandItem
          onSelect={() => {
            void handleStatusSelect(PlantMaterialStatus.Present);
          }}
          loading={
            updatePlantMaterials.variables?.input.status ===
              PlantMaterialStatus.Present && updatePlantMaterials.isPending
          }
          disabled={updatePlantMaterials.isPending}
          data-cy="present-status-menu-item"
          shortcut="P"
        >
          <Dot
            size="lg"
            color="primary"
            // eslint-disable-next-line sonarjs/no-duplicate-string
            className={twMerge(updatePlantMaterials.isPending && "opacity-50")}
          />
          Present
        </CommandItem>
        <CommandItem
          onSelect={() => {
            void handleStatusSelect(PlantMaterialStatus.Transient);
          }}
          loading={
            updatePlantMaterials.variables?.input.status ===
              PlantMaterialStatus.Transient && updatePlantMaterials.isPending
          }
          disabled={updatePlantMaterials.isPending}
          data-cy="transient-status-menu-item"
          shortcut="T"
        >
          <Dot
            size="lg"
            color="warning"
            className={twMerge(updatePlantMaterials.isPending && "opacity-50")}
          />
          Transient
        </CommandItem>
        <CommandItem
          onSelect={() => {
            void handleStatusSelect(PlantMaterialStatus.Unknown);
          }}
          loading={
            updatePlantMaterials.variables?.input.status ===
              PlantMaterialStatus.Unknown && updatePlantMaterials.isPending
          }
          disabled={updatePlantMaterials.isPending}
          data-cy="unknown-status-menu-item"
          shortcut="U"
        >
          <Dot
            size="lg"
            color="error"
            className={twMerge(updatePlantMaterials.isPending && "opacity-50")}
          />
          Unknown
        </CommandItem>
        <CommandItem
          onSelect={onAbsentSelect}
          loading={
            updatePlantMaterials.variables?.input.status ===
              PlantMaterialStatus.Absent && updatePlantMaterials.isPending
          }
          disabled={updatePlantMaterials.isPending}
          data-cy="absent-status-menu-item"
          shortcut="A"
        >
          <Dot
            size="lg"
            color="grey"
            className={twMerge(updatePlantMaterials.isPending && "opacity-50")}
          />
          Absent
        </CommandItem>
      </CommandGroup>
      <CommandEmpty>No results</CommandEmpty>
    </>
  );
};

export { CommandMenuStatusPage };
